import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { CustomerSupportFormInput } from '@optimus/models';
import {
  LoginMemberRequest,
  LoginMemberResponse,
  LogoutResponse,
  MemberRegistrationRequest,
  MemberRegistrationResponse,
} from '../../common/models/AuthenticationRequest';
import * as loginAPI from '../../api/loginAPI';
import { buildAfterLoginPath, customHistory, extractTokenExpiry } from '../../common/utils/loginUtils';
import { RootState } from '../storeSetup';
import {
  HOME_AUTH_LOGIN_ROUTE,
  ONBOARDING_AUTH_LOGIN_ROUTE,
} from '../../routes/namedRoutes';
import { SubmitSupportFormResponse } from '../../common/models/SubmitSupportFormResponse';
import { ReactCustomerIO } from '../../config/customerIo';
import { ReactChameleon } from '../../config/chameleon';
import { ReactHeap } from '../../config/heap';
import { ReactOptimizely } from '../../config/optimizely';
import { Logger } from '../../config/logging/logger';

// fallback navigation
const windowNavigation = (path: string) => {
  window.location.href = path;
};

export const logout = createAsyncThunk<
// Return type of the payload creator
LogoutResponse | null,
// First argument to the payload creator
{
  isOnboarding?: boolean;
  navigate?: NavigateFunction;
  customAfterLoginPath?: string;
},
// Types for ThunkAPI
{
  rejectValue: AxiosError;
  state: RootState;
}>(
  'auth/logout',
  async (
    { isOnboarding, navigate, customAfterLoginPath },
    { rejectWithValue, dispatch, getState },
  ) => {
    const logger = Logger.create('auth/logout');
    // get refresh token if present
    const { memberSession: { refreshToken, solutionOrgId } } = getState().onboarding;
    const isServiceNation = solutionOrgId === process.env.REACT_APP_SERVICE_NATION_SOLUTION_ID;

    // clear cache
    dispatch({ type: 'logout/LOGOUT' });

    ReactCustomerIO.reset();
    ReactChameleon.clear();
    ReactHeap.reset();
    ReactOptimizely.clearUser();

    // redirect to login page
    let loginPath;

    // Service Nation members are sent to SN's /logout (in `finally`, once the Edge
    // logout request has finished) so their SN session is ended too.
    if (!isServiceNation) {
      if (isOnboarding) {
        loginPath = ONBOARDING_AUTH_LOGIN_ROUTE;
      } else {
        loginPath = HOME_AUTH_LOGIN_ROUTE;
      }

      if (window.location.pathname !== loginPath) {
        const navigateFunc = customHistory?.navigate || navigate || windowNavigation;
        navigateFunc(
          `${loginPath}?afterLoginPath=${buildAfterLoginPath(customAfterLoginPath)}`,
        );
      }
    }

    try {
      const response = refreshToken ? await loginAPI.logoutMember(refreshToken) : null;
      logger.debug(Logger.format('logged out successfully'));
      return response as LogoutResponse;
    } catch (err: unknown) {
      logger.error(Logger.format(err));
      return rejectWithValue(err as AxiosError);
    } finally {
      if (isServiceNation) {
        // Redirecting earlier would unload the page and could cancel the Edge logout request
        windowNavigation(`${process.env.REACT_APP_SERVICE_NATION_URL}logout`);
      } else if (window.location.pathname !== loginPath) {
        // fallback to force redirecting to login page after logout
        windowNavigation(
          `${loginPath}?afterLoginPath=${buildAfterLoginPath(customAfterLoginPath)}`,
        );
      }
    }
  },
);

export const loginMember = createAsyncThunk<
// Return type of the payload creator
LoginMemberResponse,
// First argument to the payload creator
LoginMemberRequest,
// Types for ThunkAPI
{
  rejectValue: AxiosError;
}>(
  'onboarding/loginMember',
  async (request, { rejectWithValue }) => {
    try {
      const response = await loginAPI.loginMember(request);
      const tokenExpiry = extractTokenExpiry(response.accessToken);
      return { ...response, tokenExpiry } as LoginMemberResponse;
    } catch (err: unknown) {
      return rejectWithValue(err as AxiosError);
    }
  },
);

export const submitCustomerSupportForm = createAsyncThunk<
// Return type of the payload creator
SubmitSupportFormResponse,
// First argument to the payload creator
CustomerSupportFormInput,
// Types for ThunkAPI
{
  rejectValue: AxiosError;
}>(
  'submitCustomerSupportForm',
  async (request, { rejectWithValue }) => {
    try {
      const response = await loginAPI.submitCustomerSupportForm(request);
      return response as SubmitSupportFormResponse;
    } catch (err: unknown) {
      return rejectWithValue(err as AxiosError);
    }
  },
);

export const memberRegistration = createAsyncThunk<
// Return type of the payload creator
MemberRegistrationResponse,
// First argument to the payload creator
MemberRegistrationRequest,
// Types for ThunkAPI
{
  rejectValue: AxiosError;
}>(
  'onboarding/memberRegistration',
  async (request, { rejectWithValue }) => {
    try {
      const response = await loginAPI.memberRegistration(request);
      return response as MemberRegistrationResponse;
    } catch (err: unknown) {
      return rejectWithValue(err as AxiosError);
    }
  },
);
